/**
 * @fileoverview Automatically displays a popup modal after a delay if conditions are met.
 * @author
 * Dreamers of Day
 */

/**
 * AutoPopup handles automatic delayed display of a modal popup.
 */
class AutoPopup {
    // Storage key used to store shown popups in sessionStorage
    storageKey = 'auto-popup-save-list';

    // Delay in milliseconds before showing the popup
    popupDelay = 5000;

    // CSS selector used to identify the popup element
    popupSelector = '._form';

    /**
     * Constructs the AutoPopup instance and immediately attempts to register the popup.
     * @constructor
     */
    constructor() {
        // Try to register the popup logic during construction
        this.registerPopup();
    }

    /**
     * Registers the popup for automatic display after a delay.
     * Checks for popup existence before proceeding.
     * @returns {void}
     */
    registerPopup() {
        // Attempt to find the popup on the page
        const popup = this.getPopup();

        // If no popup exists, exit early
        if (!popup) return;

        // Delay execution to allow page assets to load
        setTimeout(() => this.processPopup(), this.popupDelay);
    }

    /**
     * Orchestrates the popup logic:
     * - Checks if the popup should be shown
     * - Marks the popup as shown in the session
     * - Displays the popup
     * @returns {void}
     */
    processPopup() {
        // Exit if popup has already been shown or shouldn't be shown
        if (!this.shouldShowPopup()) return;

        // Mark this popup as shown for the session
        this.markPopupAsShown();

        // Display the actual popup
        this.displayPopup();
    }

    /**
     * Determines if the popup should be shown in this session.
     * Requires a popup with an identifiable ID and no prior display.
     * @returns {boolean} True if popup should be shown; false otherwise.
     */
    shouldShowPopup() {
        const popupId = this.getPopupId();

        // Return false if no valid popup ID is found
        if (!popupId) return false;

        const storage = this.getSessionStorage();
        const storageKey = this.getStoragePopupKey(popupId);

        // Return true if the popup has not been marked as shown
        return !storage.includes(storageKey);
    }

    /**
     * Marks a popup as shown by storing its key in sessionStorage.
     * @returns {boolean} True if the popup was marked successfully; false otherwise.
     */
    markPopupAsShown() {
        const popupId = this.getPopupId();

        // If popup ID is invalid, do not proceed
        if (!popupId) return false;

        const storage = this.getSessionStorage();
        const storageKey = this.getStoragePopupKey(popupId);

        // If not already recorded, add the key to storage
        if (!storage.includes(storageKey)) {
            storage.push(storageKey);
            sessionStorage.setItem(this.storageKey, JSON.stringify(storage));
        }

        return true;
    }

    /**
     * Displays the popup by setting its display style to block.
     * This assumes the popup is hidden via CSS initially.
     * @returns {void}
     */
    displayPopup() {
        const popup = this.getPopup();

        // Ensure the popup is present before modifying it
        if (!popup) return;

        // Directly display the popup element
        popup.style.display = 'block';
    }

    /**
     * Retrieves the popup DOM element using the preset selector.
     * @returns {HTMLElement|null} The popup element if found; otherwise null.
     */
    getPopup() {
        return document.querySelector(this.popupSelector);
    }

    /**
     * Extracts the popup's unique ID from the form input.
     * @returns {string|false} ID of the popup or false if not found.
     */
    getPopupId() {
        const modalElement = this.getPopup();

        // Exit early if modal not found
        if (!modalElement) return false;

        // Look for input with name="f" inside the popup
        const inputF = modalElement.querySelector('[name="f"]');

        // Return the input's value if it exists, otherwise false
        return inputF ? inputF.value : false;
    }

    /**
     * Fetches and parses the popup display history from sessionStorage.
     * @returns {string[]} Array of previously shown popup keys.
     */
    getSessionStorage() {
        const storage = sessionStorage.getItem(this.storageKey);

        // Parse stored JSON or return empty array
        return storage ? JSON.parse(storage) : [];
    }

    /**
     * Builds a unique sessionStorage key based on page path and popup ID.
     * This prevents showing the same popup on multiple pages.
     * @param {string} popupId - The ID of the popup to generate key for.
     * @returns {string|false} A string storage key or false on failure.
     */
    getStoragePopupKey(popupId) {
        // Return false if popup ID is invalid
        if (!popupId) return false;

        // Normalize the current pathname by removing leading/trailing slashes
        let pathname = window.location.pathname.replace(/^\/+|\/+$/g, '');

        // Replace slashes with underscores for key-safe strings
        let key = pathname.replace(/\//g, '_');

        // Provide fallback name for home route
        key = key || 'home';

        // Return constructed unique key
        return `${key}--${popupId}`;
    }
}

/**
 * Initializes the AutoPopup class to handle popups automatically.
 * Should be called once per page load.
 * @returns {void}
 * @author
 * Dreamers of Day
 */
export default function autoPopup() {
    // Instantiate the AutoPopup handler (side effect driven)
    new AutoPopup();
}
